<template>
  <Dialog
    :visible="visible"
    header="Add Video"
    modal
    class="video-upload-dialog"
    :style="{ width: '30rem' }"
    :closable="!isUploading"
    :dismissableMask="!isUploading"
    @update:visible="onVisibleChange"
  >
    <div class="dialog-body-wrap">
      <div class="dialog-fields" :class="{ 'is-blurred': isServerUploading }">
        <div class="upload-field">
          <label>
            Show
            <i v-if="showsLoading" class="pi pi-spin pi-spinner loading-icon" />
          </label>
          <Dropdown
            ref="showDropdownRef"
            v-model="localShowId"
            :options="shows"
            optionLabel="show_title"
            optionValue="show_id"
            :placeholder="showsLoading ? 'Loading shows…' : 'Select a show'"
            filter
            class="w-full"
            :disabled="isUploading || showsLoading"
            :panelStyle="showDropdownPanelStyle"
          />
        </div>

        <div class="upload-field">
          <label>Video title</label>
          <InputText
            v-model="videoTitle"
            placeholder="Enter a title for this video"
            class="w-full"
            :maxlength="MAX_TITLE_LENGTH"
            :disabled="isUploading"
          />
          <p v-if="titleError" class="field-error">{{ titleError }}</p>
        </div>

        <div class="upload-field">
          <label>Video file</label>
          <div v-if="uploadSuccess" class="upload-success">
            <div class="success-check">
              <i class="pi pi-check"></i>
            </div>
            <p class="success-text">Video uploaded successfully</p>
            <div class="success-actions">
              <Button
                label="Upload more"
                icon="pi pi-plus"
                outlined
                @click="uploadMore"
              />
              <Button
                label="Go to video"
                icon="pi pi-arrow-right"
                outlined
                @click="goToVideo"
              />
            </div>
          </div>
          <template v-else>
            <div class="dropzone-wrap">
              <FilePond
                ref="pond"
                name="video"
                :accepted-file-types="ACCEPTED_VIDEO_TYPES"
                :file-validate-type-detect-type="detectVideoType"
                :max-file-size="`${MAX_FILE_SIZE_MB}MB`"
                :allow-multiple="false"
                :disabled="!canUpload || isUploading"
                :label-idle="dropLabel"
                :server="serverConfig"
                @processfilestart="isUploading = true"
                @processfile="onProcessFile"
              />
            </div>
            <p class="upload-rules">
              MP4, MOV or WEBM · Max {{ MAX_FILE_SIZE_MB }}MB
            </p>
          </template>
        </div>
      </div>

      <div v-if="isServerUploading" class="server-upload-overlay">
        <i class="pi pi-spin pi-spinner"></i>
        <span class="server-upload-text">Loading, may take a while</span>
      </div>
    </div>
  </Dialog>
</template>

<script setup>
import { ref, computed, watch, nextTick } from "vue";
import { useRouter } from "vue-router";
import Dialog from "primevue/dialog";
import Dropdown from "primevue/dropdown";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import { useToast } from "primevue/usetoast";
import vueFilePond from "vue-filepond";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size";
import "filepond/dist/filepond.min.css";
import { useMatchTriggerWidth } from "../composables/useMatchTriggerWidth";
import {
  useVideoUploadPipeline,
  ACCEPTED_VIDEO_TYPES,
  detectVideoType,
  MAX_FILE_SIZE_MB,
  MIN_TITLE_LENGTH,
  MAX_TITLE_LENGTH,
} from "../composables/useVideoUploadPipeline";

const FilePond = vueFilePond(
  FilePondPluginFileValidateType,
  FilePondPluginFileValidateSize
);

// eslint-disable-next-line no-undef
const props = defineProps({
  visible: { type: Boolean, default: false },
  shows: { type: Array, default: () => [] },
  showsLoading: { type: Boolean, default: false },
  showId: { type: [String, Number], default: null },
  token: { type: String, required: true },
});
// eslint-disable-next-line no-undef
const emit = defineEmits(["update:visible", "update:showId", "uploaded"]);

const router = useRouter();
const toast = useToast();
const pond = ref(null);
const isUploading = ref(false);
const isServerUploading = ref(false);
const uploadSuccess = ref(false);
const lastUploadedVideoId = ref(null);
const videoTitle = ref("");

const localShowId = computed({
  get: () => props.showId,
  set: (value) => emit("update:showId", value),
});

const titleError = computed(() => {
  const length = videoTitle.value.trim().length;
  if (length === 0 || length >= MIN_TITLE_LENGTH) return null;
  return `Title must be at least ${MIN_TITLE_LENGTH} characters.`;
});

const canUpload = computed(() => {
  const length = videoTitle.value.trim().length;
  return (
    !!localShowId.value &&
    length >= MIN_TITLE_LENGTH &&
    length <= MAX_TITLE_LENGTH
  );
});

const { serverConfig } = useVideoUploadPipeline({
  getToken: () => props.token,
  getShowId: () => localShowId.value,
  getTitle: () => videoTitle.value,
  toast,
  onServerUploadChange: (value) => (isServerUploading.value = value),
});

const {
  triggerEl: showDropdownRef,
  panelStyle: showDropdownPanelStyle,
  measure: measureShowDropdownWidth,
} = useMatchTriggerWidth();

watch(
  () => props.visible,
  async (visible) => {
    if (!visible) return;
    await nextTick();
    measureShowDropdownWidth();
  }
);

const dropLabel = computed(() =>
  canUpload.value
    ? `<span class="upload-drop-icon pi pi-video"></span>
       <span class="upload-drop-text">Drag &amp; drop a video here, or <span class="filepond--label-action">Browse</span></span>`
    : `<span class="upload-drop-icon pi pi-video"></span>
       <span class="upload-drop-text">Select a show and enter a title first</span>`
);

const onVisibleChange = (value) => {
  if (!value && !isUploading.value) {
    pond.value?.removeFiles();
    uploadSuccess.value = false;
    videoTitle.value = "";
  }
  emit("update:visible", value);
};

const onProcessFile = (fileError, file) => {
  isUploading.value = false;
  if (!fileError) {
    emit("uploaded");
    lastUploadedVideoId.value = file.serverId;
    uploadSuccess.value = true;
  }
};

const uploadMore = () => {
  uploadSuccess.value = false;
  videoTitle.value = "";
  pond.value?.removeFiles();
};

const goToVideo = () => {
  const videoId = lastUploadedVideoId.value;
  onVisibleChange(false);
  if (videoId) {
    router.push({ path: "/podcast-admin/videos", query: { videoId } });
  }
};
</script>

<style scoped>
.dialog-body-wrap {
  position: relative;
}

.dialog-fields.is-blurred {
  filter: blur(2px);
  pointer-events: none;
  user-select: none;
}

.server-upload-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  z-index: 5;
}

.server-upload-overlay i {
  font-size: 26px;
  color: var(--primary-color, #6366f1);
}

.server-upload-text {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-color-secondary);
}

.upload-field {
  margin-bottom: 18px;
}

.upload-field label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-color-secondary);
  margin-bottom: 6px;
}

.loading-icon {
  font-size: 11px;
  margin-left: 4px;
}

.upload-rules {
  margin: 10px 2px 0;
  font-size: 12px;
  color: var(--text-color-secondary);
}

.field-error {
  margin: 6px 2px 0;
  font-size: 12px;
  color: #ff5a57;
}

.upload-success {
  height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  border-radius: 14px;
  background-color: var(--surface-100, #f1f2f5);
}

.success-check {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #22c55e;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: success-pop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.success-check i {
  color: #fff;
  font-size: 26px;
  animation: success-check-in 0.3s ease 0.15s both;
}

.success-text {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color);
}

.success-actions {
  display: flex;
  gap: 10px;
}

@keyframes success-pop {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes success-check-in {
  0% {
    opacity: 0;
    transform: scale(0.5);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.dropzone-wrap :deep(.filepond--root) {
  width: 100%;
  height: 200px;
  margin: 0;
}

.dropzone-wrap :deep(.filepond--panel-root) {
  border-radius: 14px;
  background-color: var(--surface-100, #f1f2f5);
}

.dropzone-wrap :deep(.filepond--drop-label) {
  height: 100%;
}

.dropzone-wrap :deep(.filepond--root[data-disabled]) {
  opacity: 0.55;
}

.dropzone-wrap :deep(.filepond--root[data-disabled] .filepond--drop-label) {
  cursor: not-allowed;
}

.dropzone-wrap :deep(.filepond--root[data-disabled] .filepond--label-action) {
  pointer-events: none;
  text-decoration: none;
}

.dropzone-wrap :deep(.filepond--drop-label label) {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-align: center;
  padding: 0 16px;
}

.dropzone-wrap :deep(.upload-drop-icon) {
  font-size: 32px;
  color: var(--text-color-secondary);
}

.dropzone-wrap :deep(.filepond--credits) {
  display: none;
}

.dropzone-wrap :deep(.upload-drop-text) {
  font-size: 13px;
  line-height: 1.4;
}
</style>
